import { ColumnDef } from "@tanstack/react-table";
import { Avatar } from "@mui/material";
import { moneyFormat } from "../../../helpers/appHelpers";

// Type definitions
interface Employee {
	firstName: string;
	lastName: string;
	photoUrl?: string;
}

interface PayPeriod {
	code: string;
}

interface PartialSalaryGroupRow {
	id: number;
	name: string;
	code: string;
	description: string;
	startPayPeriod?: PayPeriod;
	expired: boolean;
	createdAt?: string;
	modifiedAt?: string;
}

interface SalaryAdvanceGroupRow {
	id: number;
	name: string;
	code: string;
	description: string;
	startPayPeriod?: PayPeriod;
	createdAt?: string;
	modifiedAt?: string;
}

// ========== Partial Salary Groups Columns ==========
export const partialSalaryGroupColumns: ColumnDef<PartialSalaryGroupRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "startPayPeriod",
		accessorKey: "startPayPeriod",
		header: "Start Pay Period",
		size: 220,
		cell: ({ row }) => row.original.startPayPeriod?.code || "--",
	},
	{
		id: "expired",
		accessorKey: "expired",
		header: "Expired",
		size: 140,
		cell: ({ row }) => (row.original.expired ? "Yes" : "No"),
	},
];

export const partialSalaryGroupColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	startPayPeriod: true,
	expired: true,
};

// ========== Salary Advance Groups Columns ==========
export const salaryAdvanceGroupColumns: ColumnDef<SalaryAdvanceGroupRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "startPayPeriod",
		accessorKey: "startPayPeriod",
		header: "Start Pay Period",
		size: 220,
		cell: ({ row }) => row.original.startPayPeriod?.code || "--",
	},
];

export const salaryAdvanceGroupColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	startPayPeriod: true,
};

// Partial Salary columns for employee partial salaries
interface PartialSalaryRow {
	id: number;
	employee?: Employee;
	startingPayPeriod?: PayPeriod;
	amount: number;
	expired: boolean;
	group?: { name: string };
	createdAt?: string;
	modifiedAt?: string;
}

export const partialSalaryColumns: ColumnDef<PartialSalaryRow>[] = [
	{
		id: "employee",
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		cell: ({ row }) => {
			const employee = row.original.employee;
			if (!employee) return "--";
			return (
				<div className="flex items-center gap-3">
					<Avatar
						src={employee.photoUrl || ""}
						alt={`${employee.firstName} ${employee.lastName}`}
						sx={{ width: 32, height: 32 }}
					/>
					<span>{`${employee.firstName} ${employee.lastName}`}</span>
				</div>
			);
		},
	},
	{
		id: "partialSalaryGroup",
		accessorKey: "group.name",
		header: "Partial Salary Group",
		size: 220,
		cell: ({ row }) => row.original.group?.name || "--",
	},
	{
		id: "startingPayPeriod",
		accessorKey: "startingPayPeriod",
		header: "Starting Pay Period",
		size: 220,
		cell: ({ row }) => row.original.startingPayPeriod?.code || "--",
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 180,
		cell: ({ row }) => moneyFormat(row.original.amount),
	},
	{
		id: "expired",
		accessorKey: "expired",
		header: "Expired",
		size: 140,
		cell: ({ row }) => (row.original.expired ? "Yes" : "No"),
	},
];

export const partialSalaryColumnVisibility: Record<string, boolean> = {
	employee: true,
	partialSalaryGroup: true,
	startingPayPeriod: true,
	amount: true,
	expired: true,
};

// Salary Advance columns for employee salary advances
interface SalaryAdvanceRow {
	id: number;
	employee?: Employee;
	startingPayPeriod?: PayPeriod;
	amount: number;
	group?: { name: string };
	createdAt?: string;
	modifiedAt?: string;
}

export const salaryAdvanceColumns: ColumnDef<SalaryAdvanceRow>[] = [
	{
		id: "employee",
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		cell: ({ row }) => {
			const employee = row.original.employee;
			if (!employee) return "--";
			return (
				<div className="flex items-center gap-3">
					<Avatar
						src={employee.photoUrl || ""}
						alt={`${employee.firstName} ${employee.lastName}`}
						sx={{ width: 32, height: 32 }}
					/>
					<span>{`${employee.firstName} ${employee.lastName}`}</span>
				</div>
			);
		},
	},
	{
		id: "salaryAdvanceGroup",
		accessorKey: "group.name",
		header: "Salary Advance Group",
		size: 220,
		cell: ({ row }) => row.original.group?.name || "--",
	},
	{
		id: "startingPayPeriod",
		accessorKey: "startingPayPeriod",
		header: "Starting Pay Period",
		size: 220,
		cell: ({ row }) => row.original.startingPayPeriod?.code || "--",
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 180,
		cell: ({ row }) => moneyFormat(row.original.amount),
	},
];

export const salaryAdvanceColumnVisibility: Record<string, boolean> = {
	employee: true,
	salaryAdvanceGroup: true,
	startingPayPeriod: true,
	amount: true,
};
